import {
  ApiError,
  type ApiErrorCode,
  type BridgeApi,
  type BridgeEvent,
  type BridgeMode,
  type BridgePreview,
  type BridgeView,
  type ChunkRef,
  type ConnectApi,
  type ConnectCode,
  type ConnectSessionRef,
  type CreatedBridge,
  type EncryptedFile,
  type GuestKeyRequest,
  type JoinRequest,
  type JoinResult,
  type Message,
  type NearbyApi,
  type NearbyEvent,
  type NearbyOfferInput,
  type NewConnectCode,
  type NewMessage,
  type OfferRef,
  type PollRequest,
  type PollResult,
  type Progress,
  type PushApi,
  type MessageRef,
  type Transport,
} from './types';

import { t } from '@/i18n';
import { apiBase, type AppPush } from '@/lib/app';
import { BridgeEventSchema, BridgePreviewSchema, BridgeViewSchema, ClaimSchema, ConnectCodeSchema, CreatedBridgeSchema, ErrorBodySchema, isErrorCode, JoinResultSchema, MessageSchema, NearbyEventSchema, OfferResultSchema, parseAs, PollResultSchema, UploadSchema, type Schema } from './schemas';
import { openStream } from './sse';

async function toApiError(response: Response): Promise<ApiError> {
  let code: ApiErrorCode = 'internal';
  let message = t('http.unknown');
  try {
    const body = parseAs(ErrorBodySchema, await response.json());
    if (body !== null) {
      if (isErrorCode(body.error)) code = body.error;
      if (body.message !== undefined) message = body.message;
    }
  } catch {
    // non-JSON error body: keep the defaults
  }
  if (response.status === 413) code = 'payload_too_large';
  if (response.status === 429) code = 'rate_limited';
  return new ApiError(code, message, response.status);
}

/** Where the request was going and what the browser said when it failed, as a short line: the host only, never the path (it holds ids). */
function unreachable(url: string, cause: unknown): string {
  let host = t('http.theServer');
  try {
    host = new URL(url, window.location.href).host;
  } catch {
    // keep the general words
  }
  // a DOMException (an aborted or blocked request) names itself like an error, without being one in every environment
  const named = typeof cause === 'object' && cause !== null ? (cause as { name?: unknown }).name : undefined;
  return t('http.unreachableDetail', { host, reason: typeof named === 'string' && named !== '' ? named : t('http.noAnswer') });
}

async function send(url: string, init: RequestInit & { token?: string }): Promise<Response> {
  const headers = new Headers(init.headers);
  if (typeof init.body === 'string') headers.set('content-type', 'application/json');
  if (init.body instanceof Uint8Array) headers.set('content-type', 'application/octet-stream');
  if (init.token !== undefined) headers.set('authorization', `Bearer ${init.token}`);
  let response: Response;
  try {
    response = await fetch(url, { ...init, headers });
  } catch (cause) {
    throw new ApiError('network', t('http.unreachable'), 0, unreachable(url, cause));
  }
  if (!response.ok) throw await toApiError(response);
  return response;
}

function unexpected(status: number): ApiError {
  return new ApiError('internal', t('http.unexpected'), status);
}

/** A request whose answer is a JSON document of a known shape. */
async function request<T>(url: string, schema: Schema<T>, init: RequestInit & { token?: string } = {}): Promise<T> {
  const response = await send(url, init);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw unexpected(response.status);
  }
  const parsed = parseAs(schema, body);
  if (parsed === null) throw unexpected(response.status);
  return parsed;
}

/** A request that answers with nothing (204). */
async function command(url: string, init: RequestInit & { token?: string } = {}): Promise<void> {
  await send(url, init);
}

/** Chunk requests are idempotent, so a dropped connection or a hiccup on the server is retried twice. */
async function retrying<T>(attempt: () => Promise<T>, tries = 3): Promise<T> {
  for (let left = tries - 1; ; left -= 1) {
    try {
      return await attempt();
    } catch (error) {
      const transient = error instanceof ApiError && (error.code === 'network' || error.status === 502 || error.status === 503 || error.status === 504);
      if (!transient || left === 0) throw error;
      await new Promise((resolve) => setTimeout(resolve, 600 * (tries - left)));
    }
  }
}

const json = (value: unknown): string => JSON.stringify(value);

/** A path under a base, each part escaped: `path(base, 'bridges', id, 'messages')`. */
function path(base: string, ...parts: readonly (string | number)[]): string {
  return [base, ...parts.map((part) => encodeURIComponent(String(part)))].join('/');
}

/** A page of the server's stream, read with a schema: a frame that does not fit it is dropped, never passed on. */
function frameReader<T>(types: readonly string[], schema: Schema<T>, put: (event: T) => void, tag?: (type: string, data: unknown) => unknown): (type: string, data: string) => void {
  return (type, data) => {
    if (!types.includes(type)) return;
    let value: unknown;
    try {
      value = JSON.parse(data);
    } catch {
      return;
    }
    const event = parseAs(schema, tag ? tag(type, value) : value);
    if (event !== null) put(event);
  };
}

const NEARBY_EVENTS = ['hello', 'peers', 'offer', 'answer', 'reveal', 'accepted', 'ready', 'closed', 'full'];
const BRIDGE_EVENTS = ['message', 'message-deleted', 'views', 'connect-request', 'presence', 'deleted'];

/**
 * Talks to the Node server over the JSON API described in docs/API.md — on this page's origin in a browser, on the
 * chosen server inside the native app (lib/app.ts).
 */
export class HttpTransport implements Transport, BridgeApi, ConnectApi, NearbyApi, PushApi {
  constructor(private readonly base = apiBase()) {}

  // ---------------------------------------------------------------- bridges

  createBridge(ttlSeconds: number, maxViews: number, mode: BridgeMode): Promise<CreatedBridge> {
    return request(path(this.base, 'bridges'), CreatedBridgeSchema, { method: 'POST', body: json({ ttlSeconds, maxViews, mode }) });
  }

  getBridge(bridgeId: string, token: string): Promise<BridgeView> {
    return request(path(this.base, 'bridges', bridgeId), BridgeViewSchema, { token });
  }

  preview(bridgeId: string): Promise<BridgePreview> {
    return request(path(this.base, 'bridges', bridgeId, 'preview'), BridgePreviewSchema);
  }

  claim(bridgeId: string): Promise<{ claimToken: string; view: BridgeView }> {
    return request(path(this.base, 'bridges', bridgeId, 'claims'), ClaimSchema, { method: 'POST' });
  }

  postMessage(bridgeId: string, token: string, message: NewMessage): Promise<Message> {
    return request(path(this.base, 'bridges', bridgeId, 'messages'), MessageSchema, { method: 'POST', token, body: json(message) });
  }

  async postFile(bridgeId: string, token: string, file: EncryptedFile, onProgress?: Progress): Promise<Message> {
    const bridge = path(this.base, 'bridges', bridgeId);
    const { uploadId, chunkBytes } = await request(`${bridge}/uploads`, UploadSchema, { method: 'POST', token, body: json({ chunks: file.chunks, size: file.size }) });
    // the file was cut before it was announced: a server that wants other chunks gets a clear refusal, not a broken upload
    if (chunkBytes !== file.chunkBytes) throw new ApiError('bad_request', t('http.chunkSize', { n: chunkBytes }));
    let sent = 0;
    onProgress?.(0);
    for (let index = 0; index < file.chunks; index += 1) {
      const bytes = await file.chunk(index);
      await retrying(() => send(path(`${bridge}/uploads`, uploadId, 'chunks', index), { method: 'PUT', token, body: bytes }));
      sent += bytes.length;
      onProgress?.(sent / file.size);
    }
    return request(`${bridge}/messages`, MessageSchema, { method: 'POST', token, body: json({ id: file.id, kind: 'file', header: file.header, upload: uploadId }) });
  }

  async getChunk({ bridgeId, token, messageId, index }: ChunkRef): Promise<Uint8Array<ArrayBuffer>> {
    const response = await retrying(() => send(path(this.base, 'bridges', bridgeId, 'messages', messageId, 'chunks', index), { token }));
    return new Uint8Array(await response.arrayBuffer());
  }

  deleteMessage({ bridgeId, token, messageId }: MessageRef): Promise<void> {
    return command(path(this.base, 'bridges', bridgeId, 'messages', messageId), { method: 'DELETE', token });
  }

  deleteBridge(bridgeId: string, token: string): Promise<void> {
    return command(path(this.base, 'bridges', bridgeId), { method: 'DELETE', token });
  }

  subscribe(bridgeId: string, token: string, onEvent: (event: BridgeEvent) => void, device?: string): () => void {
    const url = `${path(this.base, 'bridges', bridgeId, 'events')}${device === undefined ? '' : `?device=${encodeURIComponent(device)}`}`;
    return openStream(url, () => ({ authorization: `Bearer ${token}` }), {
      // a malformed frame is dropped; the next snapshot fetch heals the state
      onEvent: frameReader(BRIDGE_EVENTS, BridgeEventSchema, onEvent),
      onOpen: () => {
        onEvent({ type: 'connection', online: true });
      },
      onDown: () => {
        onEvent({ type: 'connection', online: false });
      },
    });
  }

  // ---------------------------------------------------------------- connect codes

  createConnectCode({ bridgeId, ownerToken, ownerKeyHash }: NewConnectCode): Promise<ConnectCode> {
    return request(path(this.base, 'bridges', bridgeId, 'connect-codes'), ConnectCodeSchema, { method: 'POST', token: ownerToken, body: json({ ownerKeyHash }) });
  }

  revealConnectKey({ bridgeId, ownerToken, sessionId, ownerPublicKey }: ConnectSessionRef & { readonly ownerPublicKey: string }): Promise<void> {
    return command(path(this.base, 'bridges', bridgeId, 'connect-codes', sessionId, 'reveal'), { method: 'POST', token: ownerToken, body: json({ ownerPublicKey }) });
  }

  completeConnect({ bridgeId, ownerToken, sessionId, wrappedKey }: ConnectSessionRef & { readonly wrappedKey: string }): Promise<void> {
    return command(path(this.base, 'bridges', bridgeId, 'connect-codes', sessionId, 'key'), { method: 'POST', token: ownerToken, body: json({ wrappedKey }) });
  }

  joinWithCode({ code }: JoinRequest): Promise<JoinResult> {
    return request(path(this.base, 'connect'), JoinResultSchema, { method: 'POST', body: json({ code }) });
  }

  sendGuestKey({ sessionId, pollToken, publicKey, device }: GuestKeyRequest): Promise<void> {
    return command(path(this.base, 'connect', sessionId, 'key'), { method: 'POST', token: pollToken, body: json({ publicKey, device }) });
  }

  pollConnect({ sessionId, pollToken }: PollRequest): Promise<PollResult> {
    return request(path(this.base, 'connect', sessionId), PollResultSchema, { token: pollToken });
  }

  // ---------------------------------------------------------------- nearby

  nearby(device: string, preferredName: string | null, onEvent: (event: NearbyEvent) => void, push: AppPush | null = null): () => void {
    const query = new URLSearchParams({ device });
    if (preferredName !== null) query.set('name', preferredName);
    // the app's device token goes in headers, not in the URL
    const headers: Record<string, string> = push === null ? {} : { 'x-push-token': push.token, 'x-push-env': push.env };
    let stop = (): void => undefined;
    // an event's type travels as the frame's name, not in its data
    const read = frameReader(
      NEARBY_EVENTS,
      NearbyEventSchema,
      (event) => {
        onEvent(event);
        // A full network is final for this page: stop asking.
        if (event.type === 'full') stop();
      },
      (type, data) => (typeof data === 'object' && data !== null ? { ...data, type } : data),
    );
    stop = openStream(`${path(this.base, 'nearby')}?${query.toString()}`, () => headers, {
      onEvent: read,
      onOpen: () => {
        onEvent({ type: 'connection', online: true });
      },
      onDown: () => {
        onEvent({ type: 'connection', online: false });
      },
    });
    return stop;
  }

  nearbyOffer(token: string, input: NearbyOfferInput): Promise<{ offerId: string; expiresAt: string }> {
    return request(path(this.base, 'nearby', 'offers'), OfferResultSchema, { method: 'POST', token, body: json(input) });
  }

  nearbyAnswer({ token, offerId, guestPublicKey }: OfferRef & { readonly guestPublicKey: string }): Promise<void> {
    return command(path(this.base, 'nearby', 'offers', offerId, 'answer'), { method: 'POST', token, body: json({ guestPublicKey }) });
  }

  nearbyReveal({ token, offerId, ownerPublicKey }: OfferRef & { readonly ownerPublicKey: string }): Promise<void> {
    return command(path(this.base, 'nearby', 'offers', offerId, 'reveal'), { method: 'POST', token, body: json({ ownerPublicKey }) });
  }

  nearbyAccept({ token, offerId }: OfferRef): Promise<void> {
    return command(path(this.base, 'nearby', 'offers', offerId, 'accept'), { method: 'POST', token });
  }

  nearbyDecline({ token, offerId }: OfferRef): Promise<void> {
    return command(path(this.base, 'nearby', 'offers', offerId, 'decline'), { method: 'POST', token });
  }

  nearbyKey({ token, offerId, ownerToken, wrappedKey }: OfferRef & { readonly ownerToken: string; readonly wrappedKey: string }): Promise<void> {
    return command(path(this.base, 'nearby', 'offers', offerId, 'key'), { method: 'POST', token, body: json({ ownerToken, wrappedKey }) });
  }

  // ---------------------------------------------------------------- push

  subscribePush(bridgeId: string, token: string, push: AppPush): Promise<void> {
    return command(path(this.base, 'bridges', bridgeId, 'push'), { method: 'POST', token, body: json(push) });
  }
}

export const transport: Transport = new HttpTransport();
